import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
} from "@material-ui/core";
import PropTypes from "prop-types";
import ArrowDownIcon from "./ArrowDownIcon";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "8px",
      padding: 0,
      minWidth: "500px",
      maxWidth: "500px",
      backgroundColor: "#FFFFFF",
      border: "1px solid #CED4DA",
    },
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  },
  dialogTitle: {
    padding: theme.spacing(2.5, 3),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #CED4DA",
    margin: 0,
  },
  title: {
    fontFamily: "Inter, sans-serif",
    fontSize: "20px",
    fontWeight: 600,
    color: "#212529",
    lineHeight: "28px",
    margin: 0,
  },
  closeButton: {
    padding: theme.spacing(0.5),
    color: "#495057",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  closeIcon: {
    width: 20,
    height: 20,
  },
  dialogContent: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: "1px solid #CED4DA",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  inputRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
  },
  inputWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#212529",
    marginBottom: theme.spacing(0.75),
    display: "block",
  },
  inputField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      color: "#212529",
      backgroundColor: "#FFFFFF",
      height: "40px",
      "& fieldset": {
        borderColor: "#CED4DA",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#ADB5BD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6F42C1",
        borderWidth: "1px",
      },
      "& input::placeholder": {
        color: "#ADB5BD",
        opacity: 1,
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1, 1.5),
      height: "40px",
      boxSizing: "border-box",
    },
    "& .MuiSelect-root": {
      padding: theme.spacing(1, 1.5),
      height: "40px",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      paddingLeft: theme.spacing(1.5),
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  selectField: {
    "& .MuiSelect-icon": {
      color: "#495057",
      right: theme.spacing(1.5),
    },
  },
  placeholder: {
    color: "#ADB5BD",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    paddingTop: theme.spacing(1.5),
    justifyContent: "flex-start",
    gap: theme.spacing(1.5),
    margin: 0,
  },
  cancelButton: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    padding: theme.spacing(1, 2),
    borderRadius: "6px",
    textTransform: "none",
    border: "1px solid #A78BFA",
    color: "#A78BFA",
    backgroundColor: "#FFFFFF",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "#F8F9FA",
      borderColor: "#A78BFA",
    },
  },
  createButton: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    padding: theme.spacing(1, 2),
    borderRadius: "6px",
    textTransform: "none",
    backgroundColor: "#6F42C1",
    color: "#FFFFFF",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "#5A32A3",
    },
    "&:disabled": {
      backgroundColor: "#E1E1E1",
      color: "#8F8F8F",
    },
  },
}));

/**
 * CreateBoxOrderModal Component
 *
 * Modal dialog for creating a new box order
 *
 * @param {boolean} open - Whether the modal is open
 * @param {Function} onClose - Handler for closing the modal
 * @param {Function} onCreateOrder - Handler for creating the order
 */
const CreateBoxOrderModal = ({ open, onClose, onCreateOrder }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [boxType, setBoxType] = useState("");
  const [quantity, setQuantity] = useState("");

  const handleClose = () => {
    setBoxType("");
    setQuantity("");
    onClose();
  };

  const handleCreateOrder = () => {
    if (boxType && quantity) {
      onCreateOrder({ boxType, quantity: parseInt(quantity, 10) });
      handleClose();
    }
  };

  const boxTypes = [
    { value: "A", label: "Type A" },
    { value: "B", label: "Type B" },
    { value: "C", label: "Type C" },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      aria-labelledby="create-box-order-title"
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography className={classes.title} id="create-box-order-title">
          {t("Create Box Order")}
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          aria-label="close"
        >
          <svg
            className={classes.closeIcon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.formContainer}>
          <div className={classes.inputRow}>
            <div className={classes.inputWrapper}>
              <label className={classes.label}>{t("Box Type")}</label>
              <TextField
                select
                fullWidth
                variant="outlined"
                className={`${classes.inputField} ${classes.selectField}`}
                value={boxType}
                onChange={(e) => setBoxType(e.target.value)}
                displayEmpty
                SelectProps={{
                  IconComponent: ArrowDownIcon,
                  renderValue: (selected) => {
                    if (!selected) {
                      return (
                        <span className={classes.placeholder}>
                          {t("Select box type")}
                        </span>
                      );
                    }
                    return boxTypes.find((t) => t.value === selected)?.label;
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <span className={classes.placeholder}>{t("Select box type")}</span>
                </MenuItem>
                {boxTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div className={classes.inputWrapper}>
              <label className={classes.label}>{t("Quantity")}</label>
              <TextField
                fullWidth
                variant="outlined"
                className={classes.inputField}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                type="number"
                inputProps={{ min: 1 }}
                placeholder={t("Enter quantity")}
              />
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button className={classes.cancelButton} onClick={handleClose}>
          {t("Cancel")}
        </Button>
        <Button
          className={classes.createButton}
          onClick={handleCreateOrder}
          variant="contained"
          disabled={!boxType || !quantity}
        >
          {t("Create Order")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateBoxOrderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateOrder: PropTypes.func.isRequired,
};

export default CreateBoxOrderModal;

